import { BotState } from '../Bots/BlobBot';
import AgentLauncher from '../Bots/AgentLauncher';
import StateGallery from '../Bots/StateGallery';
import MotionPlayer from '../Bots/MotionPlayer';
import TraceLine from '../Bots/TraceLine';
import ToolRoster from '../Bots/ToolRoster';
import EOS from '../../agent/persona';
import AgentStatus from '../Bots/AgentStatus';
import { toolActivity } from '../Bots/activityStates';
import type { OrbState } from 'thinking-orbs';
import { useDwellQueue } from '../../lib/useMinDwell';
import FlintChart from '../Bots/FlintChart';
import VoiceTab from './VoiceTab';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { applyPersonaTheme, resetPersonaTheme, PersonaTheme } from '../../utils/personaTheme';
import { getTracer, getTraceHeaders, recordEvent } from '../../telemetry';
import { useNavigate } from 'react-router-dom';
import { runAgent, ChatMessage, Step } from '../../agent/harness';
import { STAGE, stageAlpha } from '../../design/lab/palette';
import { ChoiceGroup } from '../../design/ui';
import BayerDither from '../../design/BayerDither';
import { ArrowRight, Close } from '../../design/icons';
import { TOOLS } from '../../agent/tools';
import ChatComposer from './ChatComposer';
import ChatReply from './ChatReply';
import { CHAT_COMMANDS, SLASH_COMMANDS } from './chatCommands';
import './chatbot.css';

interface Message {
    id: string;
    type: 'command' | 'output' | 'error' | 'system' | 'trace' | 'states' | 'tools' | 'chart' | 'player' | 'context_ir' | 'multi_path' | 'help';
    chart?: any;
    ir?: any;
    mp?: any; // multi-path detail
    text: string;
    retry?: { kind: 'chat' | 'persona'; text: string };
}

// Verified against the live Groq catalogue. The llama-3.x ids this used to
// carry are decommissioned and now 404. Only tool-calling-capable models belong
// here — the agent loop is useless without them.
const MODELS = [
    { id: 'openai/gpt-oss-120b', label: 'gpt-oss-120b' },
    { id: 'openai/gpt-oss-20b', label: 'gpt-oss-20b' },
    { id: 'qwen/qwen3.8-27b', label: 'qwen3.8-27b' },
];

const EXPERIMENTS = [
    {
        id: 'persona',
        label: '🎨  /persona',
        desc: 'Describe yourself → LLM generates a custom theme for this site',
        command: '/persona',
    },
    {
        id: 'roast',
        label: '🔥  roast my resume',
        desc: 'Brutally honest feedback on Abhijith\'s CV',
        command: 'Roast Abhijith\'s resume in a brutally honest but constructive way.',
    },
    {
        id: 'interview',
        label: '🎤  mock interview',
        desc: 'Conduct a 2-question technical interview as a hiring manager',
        command: 'Conduct a short mock technical interview — ask me 2 challenging questions as if hiring Abhijith for a Senior AI Architect role.',
    },
    {
        id: 'explain',
        label: '🧠  explain like a 5 year old',
        desc: 'What does Abhijith actually do, in plain language?',
        command: 'Explain what Abhijith does for work as if explaining to a 5 year old.',
    },
];

const SLASH_PROMPTS: Record<string, string> = {
    '/about':   'Give me a brief overview of Abhijith\'s professional background.',
    '/skills':  'What are Abhijith\'s main technical skills and expertise areas?',
    '/projects':'Tell me about Abhijith\'s most interesting projects.',
    '/contact': 'How can I get in touch with Abhijith?',
    '/persona': 'I want to try the persona experiment. Ask me to describe myself in one sentence, then tell me what theme/colour/vibe would suit me on this site.',
};

const SLASH_OPENUI_CARDS: Record<string, string> = {
    '/about': [
        '```openui-lang',
        'root = Card([header, kpis, table, follow])',
        'header = Header("Abhijith Eanuga", "Senior Systems Architect & AI Specialist · Berlin", "11+ YRS")',
        'kpis = Stack([mRole, mCerts, mLabs])',
        'mRole = Metric("Current", "Zalando SE", "2020–Now")',
        'mCerts = Metric("Credentials", "10 Certs", "Architect")',
        'mLabs = Metric("Published", "18 Notes · 30 Labs", "live")',
        'table = Table(["Company", "Role", "Location"], [r1, r2, r3, r4])',
        'r1 = Row("Zalando SE", "Senior Systems Architect", "Berlin")',
        'r2 = Row("LemonOne", "Lead Salesforce Engineer", "Berlin")',
        'r3 = Row("adidas", "Senior Platform Engineer", "Germany")',
        'r4 = Row("Infosys", "Systems Engineer", "India")',
        'follow = FollowUpBlock([f1, f2, f3])',
        'f1 = FollowUpItem("Fly 3D Career", "/cinema")',
        'f2 = FollowUpItem("Explore Skills", "/skills")',
        'f3 = FollowUpItem("Open Resume", "/about")',
        '```',
    ].join('\n'),
    '/skills': [
        '```openui-lang',
        'root = Card([header, chart, follow])',
        'header = Header("Technical Toolkit", "Enterprise platforms, agent orchestration & ML runtimes", "6 DOMAINS")',
        'chart = BarChart(["AI & Agents", "Enterprise CRM", "Cloud & DevOps", "ML & Training", "Data & RAG"], [96, 98, 90, 88, 94], "%")',
        'follow = FollowUpBlock([f1, f2, f3])',
        'f1 = FollowUpItem("See Projects", "/projects")',
        'f2 = FollowUpItem("Try Backprop Lab", "/labs/backprop-forge")',
        'f3 = FollowUpItem("Run Fit Check", "/fit")',
        '```',
    ].join('\n'),
    '/projects': [
        '```openui-lang',
        'root = Card([header, table, follow])',
        'header = Header("Selected Systems & Projects", "Production architectures and interactive browser labs", "13 SYSTEMS")',
        'table = Table(["System", "Stack", "Focus"], [p1, p2, p3, p4])',
        'p1 = Row("Hybrid RAG Platform", "BM25 · MiniLM · RRF", Tag("RAG", "good"))',
        'p2 = Row("Agentforce Orchestrator", "Bedrock · Apex REST", Tag("Agents", "good"))',
        'p3 = Row("Backprop Forge", "WASM · SIMD · TypeScript", Tag("Training", "neutral"))',
        'p4 = Row("LoRA Fine-Tuning", "PyTorch · PEFT · QLoRA", Tag("LLMs", "warn"))',
        'follow = FollowUpBlock([f1, f2])',
        'f1 = FollowUpItem("Inspect All Projects", "/about")',
        'f2 = FollowUpItem("Open Streaming GenUI Lab", "/labs/openui-streaming")',
        '```',
    ].join('\n'),
    '/contact': [
        '```openui-lang',
        'root = Card([header, kpis, follow])',
        'header = Header("Get in Touch", "Berlin, Germany · Open to hard systems & AI problems", "CONTACT")',
        'kpis = Stack([mEmail, mLoc])',
        'mEmail = Metric("Email", "eabhijith@gmail.com", "direct")',
        'mLoc = Metric("Location", "Berlin, DE", "CET")',
        'follow = FollowUpBlock([f1, f2])',
        'f1 = FollowUpItem("Open About & Links", "/about")',
        'f2 = FollowUpItem("Run Role Fit Check", "/fit")',
        '```',
    ].join('\n'),
};
// agent/persona.ts so the avatar, the greeting and the model prompt cannot
// drift apart.
const INTRO: Message[] = [{ id: 'intro', type: 'system', text: EOS.greeting.join('\n') }];

interface AIChatbotProps {
    mode?: string;
    setMode?: (m: 'light' | 'dark' | 'system') => void;
}

const AIChatbot: React.FC<AIChatbotProps> = ({ mode = 'system', setMode }) => {
    const [open, setOpen] = useState(false);
    const [minimised, setMinimised] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [tab, setTab] = useState<'chat' | 'voice'>('chat');
    const [model, setModel] = useState(MODELS[0].id);
    const [messages, setMessages] = useState<Message[]>([...INTRO]);
    const [chatHistory, setChatHistory] = useState<Array<{ role: string; content: string }>>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [commandHistory, setCommandHistory] = useState<string[]>([]);
    const [awaitingPersona, setAwaitingPersona] = useState(false);
    const [chatIcon, setChatIcon] = useState('>_');
    // The assistant's avatar carries its status, the way the fleet's do:
    // idle while waiting on you, thinking while the request is in flight, and a
    // short beat of 'done' when the answer lands.
    const navigate = useNavigate();
    // Which tool is doing what, right now. The roster reads this; the harness
    // writes it. Keyed by tool name so a new tool needs no change here.
    const [toolStates, pushToolStates, resetToolStates] = useDwellQueue<Record<string, BotState>>({}, 750);
    const toolStatesRef = useRef<Record<string, BotState>>({});
    const clearToolStates = () => {
        toolStatesRef.current = {};
        resetToolStates({});
    };
    const showToolState = (name: string, state: BotState) => {
        toolStatesRef.current = { ...toolStatesRef.current, [name]: state };
        pushToolStates(toolStatesRef.current);
    };
    // States are PUSHED as they happen rather than derived from a render:
    // React batches rapid updates, so a 'working' between two 'thinking's is
    // never rendered at all and a derived hook could not see it.
    // State and its explanation are ONE fact. Dwelling them separately lets the
    // label describe a state the avatar has already left.
    type Beat = { state: BotState; detail: string | null; activity?: OrbState };
    const [beat, pushBeat, resetBeat] = useDwellQueue<Beat>({ state: 'idle', detail: null }, 750);
    const shownState: BotState = beat.state === 'idle' && input.trim() && !loading ? 'curious' : beat.state;
    const logRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const followLog = useRef(true);
    const [showLatest, setShowLatest] = useState(false);
    const requestRef = useRef<{ controller: AbortController; kind: 'chat' | 'persona'; text: string } | null>(null);
    const experimentPick = useRef(false);
    const sessionId = useRef(`sess_${Date.now().toString(36)}`);

    useEffect(() => {
        const log = logRef.current;
        if (!log) return;
        if (followLog.current) log.scrollTo({ top: log.scrollHeight, behavior: 'auto' });
        setShowLatest(log.scrollHeight - log.scrollTop - log.clientHeight > 48);
    }, [messages, loading, open, minimised, tab]);

    useEffect(() => {
        if (!open) return;
        const opener = document.activeElement as HTMLElement | null;
        inputRef.current?.focus({ preventScroll: true });
        return () => opener?.focus();
    }, [open]);

    useEffect(() => {
        if (open && !minimised && tab === 'chat') inputRef.current?.focus({ preventScroll: true });
    }, [open, minimised, tab]);

    useEffect(() => () => {
        const request = requestRef.current;
        requestRef.current = null;
        request?.controller.abort();
    }, []);

    // Listen for persona-applied events to update chatIcon via React state (not DOM mutation)
    useEffect(() => {
        const handler = (e: Event) => {
            const { chatIcon: icon } = (e as CustomEvent).detail;
            setChatIcon(icon || '>_');
        };
        window.addEventListener('persona-applied', handler);
        return () => window.removeEventListener('persona-applied', handler);
    }, []);


    const handleApplyTheme = useCallback((theme: PersonaTheme) => {
        applyPersonaTheme(theme);
    }, []);

    const resetTheme = useCallback(() => {
        resetPersonaTheme();
    }, []);

    const addMessage = useCallback((msg: Omit<Message, 'id'>) => {
        setMessages(prev => [...prev, { ...msg, id: Date.now().toString() + Math.random() }]);
    }, []);

    const send = async (overrideText?: string) => {
        const raw = (overrideText ?? input).trim();
        if (!raw || requestRef.current) return;

        followLog.current = true;
        setInput('');
        setCommandHistory(prev => [raw, ...prev.slice(0, 49)]);

        addMessage({ type: 'command', text: raw });

        // Experiment picker — user typed a number after /experiments
        if (experimentPick.current && /^\d+$/.test(raw)) {
            const experiment = EXPERIMENTS[Number(raw) - 1];
            if (!experiment) {
                addMessage({ type: 'error', text: `Choose 1–${EXPERIMENTS.length}, or type another command.` });
                return;
            }
            experimentPick.current = false;
            if (experiment.command.startsWith('/')) await send(experiment.command);
            else await callApi(experiment.command);
            return;
        }
        experimentPick.current = false;

        // Step 2 of persona flow — user has described themselves
        if (awaitingPersona && !raw.startsWith('/')) {
            setAwaitingPersona(false);
            await callPersona(raw);
            return;
        }

        // Handle slash commands
        if (raw.startsWith('/')) {
            setAwaitingPersona(false);
            const cmd = raw.split(' ')[0].toLowerCase();
            if (cmd === '/clear') {
                clearConversation();
                return;
            }
            if (cmd === '/help') {
                addMessage({ type: 'help', text: SLASH_COMMANDS['/help'] });
                return;
            }
            if (cmd === '/theme' && setMode) {
                const next = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
                setMode(next);
                addMessage({ type: 'system', text: `# theme set to: ${next}` });
                return;
            }
            if (cmd === '/reset-theme') {
                resetTheme();
                addMessage({ type: 'system', text: '# persona theme reset' });
                return;
            }
            if (cmd === '/experiments') {
                addMessage({ type: 'system', text: '# available experiments' });
                addMessage({ type: 'output', text:
                    EXPERIMENTS.map((e, i) => `${i + 1}.  ${e.label}\n    ${e.desc}`).join('\n\n') +
                    '\n\ntype a number (1-' + EXPERIMENTS.length + ') to start'
                });
                experimentPick.current = true;
                return;
            }
            if (cmd === '/persona') {
                // Step 1: ask for description
                setAwaitingPersona(true);
                addMessage({ type: 'system', text: '# persona experiment started' });
                addMessage({ type: 'output', text: 'Describe yourself in one sentence — I\'ll design a theme just for you.\n\nExample: "I\'m a Gen Z dev who loves bold experimental design"' });
                return;
            }
            if (cmd === '/tools') {
                addMessage({ type: 'system', text: '# tools the agent can call' });
                addMessage({ type: 'tools', text: '' });
                addMessage({ type: 'output', text:
                    `${TOOLS.length} tools are available. Eos shows each call and its result in the conversation.` });
                return;
            }
            if (cmd === '/states') {
                addMessage({ type: 'system', text: '# avatar motion system' });
                addMessage({ type: 'player', text: '' });
                addMessage({ type: 'states', text: '' });
                addMessage({ type: 'output', text:
                    'The avatar carries the lifecycle; a dotted orb shows activity beside the status text.\n' +
                    'Watch the difference between waiting and blocked: waiting drifts and looks away,\n' +
                    'blocked strains in place. A frozen avatar would read as crashed, which is exactly\n' +
                    'the ambiguity this is here to remove.' });
                return;
            }
            if (cmd === '/genui') {
                addMessage({ type: 'system', text: '# openui-lang 1.0 generative ui' });
                addMessage({
                    type: 'output',
                    text: [
                        '```openui-lang',
                        'root = Card([header, kpis, chart, follow])',
                        'header = Header("Abhijith Systems Snapshot", "Rendered live via OpenUI Lang 1.0", "GENUI")',
                        'kpis = Stack([mArticles, mLabs, mSaved])',
                        'mArticles = Metric("Working Notes", "18", "published")',
                        'mLabs = Metric("Browser Labs", "30", "interactive")',
                        'mSaved = Metric("Token Delta", "-56%", "vs JSON")',
                        'chart = BarChart(["RAG & Search", "Inference", "Training", "Visualisers"], [94, 92, 88, 90], "%")',
                        'follow = FollowUpBlock([f1, f2, f3])',
                        'f1 = FollowUpItem("Open GenUI Lab", "/labs/openui-streaming")',
                        'f2 = FollowUpItem("Explore Experience", "/about")',
                        'f3 = FollowUpItem("Skills Breakdown", "/skills")',
                        '```',
                        '',
                        'Open the [Streaming Generative UI lab](/labs/openui-streaming) to scrub the token stream and apply live statement patches.',
                    ].join('\n'),
                });
                return;
            }
            if (SLASH_COMMANDS[cmd]) {
                addMessage({ type: 'system', text: `# ${SLASH_COMMANDS[cmd]}` });
                if (SLASH_OPENUI_CARDS[cmd]) {
                    addMessage({ type: 'output', text: SLASH_OPENUI_CARDS[cmd] });
                }
                if (SLASH_PROMPTS[cmd]) await callApi(SLASH_PROMPTS[cmd]);
                return;
            }
            addMessage({ type: 'error', text: `Unknown command: ${cmd}. Type / to see available commands.` });
            return;
        }

        await callApi(raw);
    };

    const clearConversation = () => {
        if (requestRef.current) return;
        followLog.current = true;
        experimentPick.current = false;
        setMessages([...INTRO]);
        setChatHistory([]);
        setAwaitingPersona(false);
        setInput('');
        clearToolStates();
        resetBeat({ state: 'idle', detail: null });
        inputRef.current?.focus({ preventScroll: true });
    };

    const stopResponse = () => {
        const request = requestRef.current;
        if (!request) return;
        requestRef.current = null;
        request.controller.abort();
        setLoading(false);
        setAwaitingPersona(request.kind === 'persona');
        setInput(draft => draft || request.text);
        clearToolStates();
        resetBeat({ state: 'idle', detail: 'stopped' });
        addMessage({ type: 'system', text: '# Response stopped. Edit your question and send again.' });
        inputRef.current?.focus({ preventScroll: true });
    };

    const callApi = async (question: string): Promise<string> => {
        if (requestRef.current) return '';
        const request = { controller: new AbortController(), kind: 'chat' as const, text: question };
        requestRef.current = request;
        const isCurrent = () => requestRef.current === request;
        setLoading(true);
        clearToolStates();
        const startingBeat: Beat = { state: 'thinking', detail: 'working out what to do' };
        resetBeat({ state: 'idle', detail: null });
        pushBeat(startingBeat);
        let lastBeat = startingBeat;
        const showBeat = (next: Beat) => {
            if (lastBeat.state === next.state && lastBeat.detail === next.detail && lastBeat.activity === next.activity) return;
            lastBeat = next;
            pushBeat(next);
        };
        const span = getTracer().startSpan('chat.agent_turn');
        const history: ChatMessage[] = [...chatHistory as ChatMessage[], { role: 'user', content: question }];

        // Each step prints a trace; avatar, activity and explanation dwell as
        // one fact so a fast tool call cannot leave mismatched status signals.
        //
        // `waiting` exists specifically so a slow model call cannot be mistaken
        // for a crash. Three animated dots cannot tell you whether the agent is
        // working or stuck; a state change can.
        let slowTimer: ReturnType<typeof setTimeout> | undefined;
        const armSlowTimer = () => {
            clearTimeout(slowTimer);
            slowTimer = setTimeout(() => {
                if (!isCurrent()) return;
                showBeat({ state: 'waiting', detail: 'the model is taking its time' });
            }, 5000);
        };

        const onStep = (step: Step) => {
            if (!isCurrent()) return;
            if (step.kind === 'context_ir') {
                if (step.detail) {
                    addMessage({ type: 'context_ir', text: step.text, ir: step.detail });
                }
                return;
            }
            if ((step.kind as string) === 'multi_path') {
                // Only show the result step (has detail), not the initial "running…" step
                if (step.detail) {
                    addMessage({ type: 'multi_path', text: step.text, mp: step.detail });
                }
                return;
            }
            if (step.kind === 'thinking') {
                showBeat({ state: 'thinking', detail: 'working out what to do' });
                armSlowTimer();
                return;
            }
            if (step.kind === 'tool_call') {
                clearTimeout(slowTimer);
                showBeat({ state: 'working', detail: step.tool ? `running ${step.tool}` : 'running a tool', activity: toolActivity(step.tool) });
                if (step.tool) showToolState(step.tool, 'working');
                addMessage({ type: 'trace', text: `\u2192 ${step.text}` });
                return;
            }
            if (step.kind === 'tool_result') {
                const d: any = step.detail;
                if (step.tool === 'chart' && d?.chart) {
                    addMessage({ type: 'chart', text: '', chart: d });
                }
                if (step.tool) {
                    const name = step.tool;
                    showToolState(name, 'done');
                }
                addMessage({ type: 'trace', text: `  \u2713 ${step.text}` });
                return;
            }
            if (step.kind === 'answer') {
                clearTimeout(slowTimer);
                showBeat({ state: 'done', detail: 'answered' });
                return;
            }
            if (step.kind === 'error') {
                clearTimeout(slowTimer);
                showBeat({ state: 'blocked', detail: step.tool ? `${step.tool} failed` : 'something went wrong' });
                if (step.tool) showToolState(step.tool, 'blocked');
                addMessage({ type: 'error', text: step.text });
            }
        };

        try {
            const result = await runAgent(
                history,
                {
                    navigate: (to: string) => { if (isCurrent()) navigate(to); },
                    setMode: setMode ? next => { if (isCurrent()) setMode(next); } : undefined,
                },
                {
                    model,
                    signal: request.controller.signal,
                    sessionId: sessionId.current,
                    pageTitle: document.title,
                    pageContent: (document.querySelector('article, main, .article-content, [class*="article"]') as HTMLElement)?.innerText?.slice(0, 3000) || '',
                    // gpt-oss-120b has 8k TPM — skip context-ir to avoid 429s
                    skipContextIR: model.includes('120b'),
                    // multi-path runs sampler + evolved arms — enable on qwen (higher TPM)
                    multiPath: model.includes('qwen'),
                },
                onStep,
            );
            if (!isCurrent()) return '';

            const toolsUsed = result.steps.filter(x => x.kind === 'tool_call').length;
            span.setAttributes({ 'agent.steps': result.steps.length, 'agent.tool_calls': toolsUsed });
            recordEvent('chat.answer', { 'agent.tool_calls': String(toolsUsed) });

            addMessage({ type: 'output', text: result.answer || '(empty response)' });
            setChatHistory(result.messages.slice(-24) as any);
            return result.answer || '';
        } catch (err: any) {
            if (!isCurrent() || request.controller.signal.aborted) return '';
            showBeat({ state: 'blocked', detail: 'the request failed' });
            addMessage({ type: 'error', text: `Request failed: ${err?.message || 'could not reach the assistant'}`, retry: { kind: 'chat', text: question } });
            return '';
        } finally {
            clearTimeout(slowTimer);
            span.end();
            if (isCurrent()) {
                requestRef.current = null;
                setLoading(false);
                toolStatesRef.current = {};
                pushToolStates({});
            }
        }
    };

    const callPersona = async (description: string) => {
        if (requestRef.current) return;
        const request = { controller: new AbortController(), kind: 'persona' as const, text: description };
        requestRef.current = request;
        const isCurrent = () => requestRef.current === request;
        setLoading(true);
        clearToolStates();
        resetBeat({ state: 'idle', detail: null });
        pushBeat({ state: 'working', detail: 'creating your site theme', activity: 'shaping' });
        addMessage({ type: 'system', text: '# generating your theme...' });
        try {
            const res = await fetch('/api/persona', {
                method: 'POST',
                signal: request.controller.signal,
                headers: {
                    'Content-Type': 'application/json',
                    ...getTraceHeaders(),
                },
                body: JSON.stringify({ description }),
            });
            const data = await res.json();
            if (!isCurrent()) return;
            if (!res.ok || !data.theme) {
                pushBeat({ state: 'blocked', detail: 'theme generation failed' });
                addMessage({ type: 'error', text: `Theme generation failed: ${data.error || 'please try again'}`, retry: { kind: 'persona', text: description } });
                return;
            }
            const theme = data.theme;
            handleApplyTheme(theme);
            pushBeat({ state: 'done', detail: 'theme applied' });
            recordEvent('persona.apply', {
                'theme.name': theme?.name || 'unknown',
                'theme.font': theme?.font || 'default',
                'theme.has_custom_css': !!theme?.customCSS,
            });
            addMessage({ type: 'output', text:
                `✓ theme applied: ${theme.name || 'custom'}\n\n` +
                `"${theme.heroText || 'Welcome'}"\n\n` +
                `primary: ${theme.primary}  bg: ${theme.bg}\n` +
                `type /reset-theme to go back to default`
            });
        } catch {
            if (!isCurrent() || request.controller.signal.aborted) return;
            pushBeat({ state: 'blocked', detail: 'theme generation failed' });
            addMessage({ type: 'error', text: 'Could not create your theme. Please try again.', retry: { kind: 'persona', text: description } });
        } finally {
            if (isCurrent()) {
                requestRef.current = null;
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        if (loading || beat.state !== 'done') return;
        const timer = setTimeout(() => resetBeat({ state: 'idle', detail: null }), 1800);
        return () => clearTimeout(timer);
    }, [loading, beat.state, resetBeat]);

    return (
        <>
            {/* The resident agent. Presence as interface: the avatar is the
                control and the status light at once. */}
            <AgentLauncher
                label={minimised ? `Restore ${EOS.name}, the site agent` : open ? `Close ${EOS.name}, the site agent` : `Open ${EOS.name}, the site agent`}
                state={shownState}
                open={open}
                onToggle={() => {
                    if (minimised) setMinimised(false);
                    else setOpen(o => !o);
                }}
                hint={chatIcon !== '>_' ? `${chatIcon} ${EOS.tagline}` : EOS.tagline}
            />

            {open && (
                <section id="ai-chatbot-panel" className={`chatbot-panel${expanded ? ' chatbot-panel--expanded' : ''}${minimised ? ' chatbot-panel--minimised' : ''}`}
                    style={{ '--terminal-accent': EOS.color } as React.CSSProperties}
                    role="dialog" aria-label={`${EOS.name}, site assistant`}
                    onKeyDown={event => {
                        if (event.key === 'Escape') {
                            const tools = event.currentTarget.querySelector<HTMLDetailsElement>('details[open]');
                            if (tools) {
                                event.preventDefault(); event.stopPropagation();
                                tools.open = false;
                                tools.querySelector('summary')?.focus();
                                return;
                            }
                            event.stopPropagation(); setOpen(false); setMinimised(false);
                        }
                    }}>
                    <header className="chatbot-panel__header">
                        <div className="chatbot-panel__window" role="group" aria-label="Assistant window">
                            <button type="button" className="chatbot-panel__control chatbot-panel__control--close" aria-label="Close assistant" title="Close assistant" onClick={() => { setOpen(false); setMinimised(false); }}><span><Close size={10} /></span></button>
                            <button type="button" className="chatbot-panel__control chatbot-panel__control--minimise" aria-label={minimised ? 'Restore' : 'Minimise'} title={minimised ? 'Restore' : 'Minimise'} onClick={() => setMinimised(m => !m)}><span><svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 5h6" fill="none" stroke="currentColor" strokeWidth="1.3" /></svg></span></button>
                            <button type="button" className="chatbot-panel__control chatbot-panel__control--expand" aria-label={expanded ? 'Compact' : 'Expand'} title={expanded ? 'Compact' : 'Expand'} aria-pressed={expanded} onClick={() => setExpanded(e => !e)}><span><svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><rect x="2" y="2" width="6" height="6" fill="none" stroke="currentColor" strokeWidth="1.2" /></svg></span></button>
                        </div>
                        <h2 className="chatbot-panel__title">
                            {EOS.handle} <span>~</span>
                        </h2>
                        {!minimised && <>
                            <ChoiceGroup label="Assistant mode" value={tab} onChange={setTab}
                                options={[{ value: 'chat', label: <><span aria-hidden="true">&gt;_ </span>Chat</> }, { value: 'voice', label: 'Voice' }]} />
                        </>}
                    </header>

                    {/* Live agent status — only in chat tab */}
                    {!minimised && tab === 'chat' && (
                        <div className="chatbot-panel__status-row"><div className="chatbot-panel__status" role="status" aria-atomic="true"><AgentStatus
                            state={shownState}
                            name={EOS.name}
                            color={EOS.color}
                            variant={EOS.variant}
                            detail={beat.detail}
                            activity={beat.activity}
                            size={28}
                        /></div>
                            {loading && <button type="button" className="chatbot-panel__stop" onClick={stopResponse}>Stop response</button>}
                        </div>
                    )}

                    {/* Voice tab */}
                    {!minimised && tab === 'voice' && (
                        <VoiceTab
                            addMessage={addMessage}
                            callApi={callApi}
                            loading={loading}
                            agentState={shownState}
                            voiceHistory={chatHistory.slice(-4) as Array<{role:string;content:string}>}
                        />
                    )}

                    {/* Chat tab content */}
                    {!minimised && tab === 'chat' && (<>

                    {/* Output */}
                    <div className="chatbot-panel__log-area">
                    <div ref={logRef} role="log" aria-label="Conversation" className="chatbot-panel__log" onScroll={event => {
                        const log = event.currentTarget;
                        followLog.current = log.scrollHeight - log.scrollTop - log.clientHeight < 48;
                        setShowLatest(!followLog.current);
                    }}>
                        {messages.map((msg, mi) => msg.type === 'player' ? (
                            <MotionPlayer key={msg.id} />
                        ) : msg.type === 'states' ? (
                            <StateGallery key={msg.id} />
                        ) : msg.type === 'tools' ? (
                            <ToolRoster key={msg.id} verbose size={30} />
                        ) : msg.type === 'chart' && msg.chart ? (
                            <FlintChart
                                key={msg.id}
                                input={msg.chart.chart}
                                title={msg.chart.title}
                                caption={msg.chart.caption}
                            />
                        ) : msg.type === 'help' ? (
                            <section className="chatbot-panel__help" key={msg.id} aria-label="Available commands">
                                <p className="chatbot-panel__help-heading">Choose a command, then send it.</p>
                                {CHAT_COMMANDS.map(command => <button type="button" key={command.command} disabled={loading} onClick={() => {
                                    setInput(command.command); inputRef.current?.focus({ preventScroll: true });
                                }}><code>{command.command}</code><span>{command.description}</span><ArrowRight size={14} /></button>)}
                            </section>
                        ) : msg.type === 'output' ? (
                            <ChatReply
                                key={msg.id}
                                text={msg.text}
                                onAction={(_label, payload) => {
                                    if (!payload) return;
                                    if (payload in SLASH_COMMANDS) {
                                        void send(payload);
                                    } else if (payload.startsWith('/')) {
                                        navigate(payload);
                                    } else {
                                        void send(payload);
                                    }
                                }}
                            />
                        ) : msg.type === 'context_ir' ? (
                            <div key={msg.id} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '8px',
                                padding: '5px 8px', margin: '4px 0',
                                background: stageAlpha(STAGE.violet, 0.06),
                                border: `1px solid ${stageAlpha(STAGE.violet, 0.15)}`,
                                borderRadius: '6px', fontSize: '11px',
                                color: STAGE.muted, fontFamily: 'inherit',
                            }}>
                                <span style={{ color: STAGE.violet, flexShrink: 0, fontSize: '10px', marginTop: '1px' }}>◈ ctx-ir</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1 }}>
                                    <span style={{ color: STAGE.ink }}>{msg.ir?.intent ?? msg.text}</span>
                                    {msg.ir?.audience && msg.ir.audience !== 'unknown' && (
                                        <span style={{
                                            background: stageAlpha(STAGE.violet, 0.15),
                                            borderRadius: '3px', padding: '0 5px',
                                            color: STAGE.violet, fontSize: '10px',
                                        }}>{msg.ir.audience}</span>
                                    )}
                                    {msg.ir?.tone_hint && (
                                        <span style={{
                                            background: stageAlpha(STAGE.green, 0.08),
                                            borderRadius: '3px', padding: '0 5px',
                                            color: STAGE.green, fontSize: '10px',
                                        }}>{msg.ir.tone_hint}</span>
                                    )}
                                    {msg.ir?._error && (
                                        <span style={{ color: STAGE.red, fontSize: '10px' }}>degraded</span>
                                    )}
                                </div>
                            </div>
                        ) : msg.type === 'multi_path' ? (
                            <div key={msg.id} style={{
                                display: 'flex', alignItems: 'flex-start', gap: '8px',
                                padding: '5px 8px', margin: '4px 0',
                                background: stageAlpha(STAGE.yellow, 0.04),
                                border: `1px solid ${stageAlpha(STAGE.yellow, 0.15)}`,
                                borderRadius: '6px', fontSize: '11px',
                                color: STAGE.muted, fontFamily: 'inherit',
                            }}>
                                <span style={{ color: STAGE.yellow, flexShrink: 0, fontSize: '10px', marginTop: '1px' }}>∫ path</span>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', flex: 1 }}>
                                    {(msg.mp?.candidates || []).map((c: any) => (
                                        <span key={c.arm} style={{
                                            background: c.arm === msg.mp?.winner
                                                ? stageAlpha(STAGE.green, 0.12)
                                                : 'rgba(255,255,255,0.04)',
                                            border: `1px solid ${c.arm === msg.mp?.winner ? stageAlpha(STAGE.green, 0.3) : 'rgba(255,255,255,0.08)'}`,
                                            borderRadius: '3px', padding: '0 6px',
                                            color: c.arm === msg.mp?.winner ? STAGE.green : STAGE.muted,
                                            fontSize: '10px',
                                        }}>
                                            {c.arm} S={c.score?.toFixed(3)}
                                            {c.arm === msg.mp?.winner && ' ✓'}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ) : msg.type === 'trace' ? (
                            <TraceLine
                                key={msg.id}
                                text={msg.text.replace(/^[\s\u2192\u2713]+/, '')}
                                kind={msg.text.trimStart().startsWith('\u2192') ? 'call' : 'result'}
                                accent={STAGE.cyan}
                                last={messages[mi + 1]?.type !== 'trace'}
                            />
                        ) : (
                            <div key={msg.id} className={`chatbot-panel__message chatbot-panel__message--${msg.type}${msg.id === 'intro' ? ' chatbot-panel__welcome' : ''}`} style={msg.id === 'intro' ? { position: 'relative', overflow: 'hidden' } : undefined}>
                                {msg.id === 'intro' && <BayerDither />}
                                {msg.type === 'command' && <span className="chatbot-panel__command-prompt"><span className="chatbot-panel__sr-only">You: </span><span aria-hidden="true">❯ </span></span>}
                                <span style={{ position: 'relative', zIndex: 1 }}>{msg.text}</span>
                                {msg.id === 'intro' && <div className="chatbot-panel__starters" role="group" aria-label="Try a question" style={{ position: 'relative', zIndex: 1 }}>
                                    <button type="button" disabled={loading} onClick={() => { void send('/about'); }}>Experience <ArrowRight size={14} /></button>
                                    <button type="button" disabled={loading} onClick={() => { void send('/genui'); }}>Live GenUI <ArrowRight size={14} /></button>
                                    <button type="button" disabled={loading} onClick={() => { void send('/projects'); }}>Projects <ArrowRight size={14} /></button>
                                </div>}
                                {msg.retry && <button type="button" className="chatbot-panel__retry" disabled={loading} onClick={() => {
                                    const retry = msg.retry!;
                                    setMessages(previous => previous.map(message => message.id === msg.id ? { ...message, retry: undefined } : message));
                                    followLog.current = true;
                                    addMessage({ type: 'system', text: '# Retrying…' });
                                    if (retry.kind === 'persona') void callPersona(retry.text);
                                    else void callApi(retry.text);
                                }}>Try again <ArrowRight size={14} /></button>}
                            </div>
                        ))}
                    </div>
                    {showLatest && <button type="button" className="chatbot-panel__latest" onClick={() => {
                        followLog.current = true;
                        logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: 'auto' });
                        setShowLatest(false);
                    }}>Latest messages <ArrowRight size={14} style={{ transform: 'rotate(90deg)' }} /></button>}
                    </div>

                    <div className="chatbot-panel__utility">
                        <button type="button" disabled={loading} onClick={() => { setInput('/'); inputRef.current?.focus({ preventScroll: true }); }}>Commands <code>/</code></button>
                        <details className="chatbot-panel__tools">
                            <summary>Tools <span>{TOOLS.length}</span></summary>
                            <div className="chatbot-panel__tools-body"><ToolRoster states={toolStates} size={20} /></div>
                        </details>
                        <label className="chatbot-panel__model">Model
                            <select value={model} disabled={loading} onChange={e => setModel(e.target.value)}>
                                {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                            </select>
                        </label>
                        <button type="button" className="chatbot-panel__clear" disabled={loading || messages.length === INTRO.length} onClick={clearConversation} aria-label="Clear conversation">Clear</button>
                    </div>
                    <ChatComposer value={input} onChange={setInput} onSend={text => { void send(text); }}
                        loading={loading} inputRef={inputRef} commandHistory={commandHistory} />
                </>)}
                </section>
            )}

            <style>{`
                @keyframes chatSlideUp {
                    from { opacity:0; transform:translateY(10px); }
                    to { opacity:1; transform:translateY(0); }
                }
                @keyframes blink {
                    0%,100% { opacity:1; } 50% { opacity:0; }
                }
            `}</style>
        </>
    );
};

export default AIChatbot;
